import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Users, ClipboardCheck, Star, Clock, 
  Plus, Search, Loader2, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ScheduleReviewModal, PerformanceReviewDetail } from './performance';
import { api } from '@/utils/apiClient';

// Role badge colors
const ROLE_COLORS = {
  owner: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  supervisor: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  cashier: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  server: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  barista: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  kitchen: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  juicer: 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-300',
  counter: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  dishwasher: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  security: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  display: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
};

// All available roles for filter dropdown (Owner excluded - they don't have performance reviews)
// Note: Manager and Supervisor roles have been combined into 'mgr-sup' as of June 2026
const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'mgr-sup', label: 'Mgr-Sup' },
  { value: 'cashier', label: 'Cashier' },
  { value: 'server', label: 'Server' },
  { value: 'barista', label: 'Barista' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'juicer', label: 'Juicer' },
  { value: 'counter', label: 'Counter' },
  { value: 'dishwasher', label: 'Dishwasher' },
  { value: 'security', label: 'Security' },
  { value: 'display', label: 'Display' },
];

/**
 * Staff Performance Reviews Tab
 * Manage performance reviews for all staff members
 */
export default function StaffPerformanceTab({ currentUser }) {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [activeReview, setActiveReview] = useState(null);

  const fetchStaffList = useCallback(async () => {
    try {
      const res = await api.get('/admin/performance/staff-list');
      setStaffList(res.data.staff || []);
    } catch (err) {
      console.error('Failed to fetch staff list:', err);
      toast.error('Failed to load staff list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaffList();
  }, [fetchStaffList]);

  const filteredStaff = useMemo(() => {
    return staffList.filter(staff => {
      const matchesSearch = staff.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           staff.email?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = filterRole === 'all' || staff.role === filterRole;
      return matchesSearch && matchesRole;
    });
  }, [staffList, searchQuery, filterRole]);

  const handleScheduleReview = async (staffId, scheduledDate, reviewType) => {
    try {
      const res = await api.post('/admin/performance/reviews/schedule', {
        staff_id: staffId,
        scheduled_date: scheduledDate,
        reviewer_id: currentUser?.id || 'admin',
        reviewer_name: currentUser?.name || 'Administrator',
        review_type: reviewType
      });
      
      if (res.data.success) {
        toast.success('Review scheduled successfully');
        setShowScheduleModal(false);
        setSelectedStaff(null);
        fetchStaffList();
      }
    } catch (err) {
      toast.error('Failed to schedule review');
    }
  };

  const handleViewReview = async (staffId) => {
    try {
      const res = await api.get(`/admin/performance/reviews/staff/${staffId}`);
      
      if (res.data.reviews && res.data.reviews.length > 0) {
        const latestReview = res.data.reviews[0];
        setActiveReview(latestReview);
      } else {
        toast.info('No reviews found for this staff member');
      }
    } catch (err) {
      toast.error('Failed to load review');
    }
  };

  // Show review detail view
  if (activeReview) {
    return (
      <PerformanceReviewDetail 
        review={activeReview} 
        onBack={() => { setActiveReview(null); fetchStaffList(); }}
        currentUser={currentUser}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6" data-testid="staff-performance-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ClipboardCheck className="w-6 sm:w-7 h-6 sm:h-7 text-teal-600" />
            Performance Reviews
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Schedule and conduct staff performance reviews
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search staff by name or email..."
              className="pl-10"
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {ROLE_OPTIONS.map(role => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Staff List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                    Staff Member
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase hidden sm:table-cell">
                    Last 30 Days
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase hidden md:table-cell">
                    Last Review
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase hidden lg:table-cell">
                    Rating
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredStaff.map((staff) => (
                  <tr key={staff.staff_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{staff.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{staff.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium capitalize ${ROLE_COLORS[staff.role] || 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'}`}>
                        {staff.role}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {staff.recent_actions_30d} actions
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      {staff.last_review_date ? (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(staff.last_review_date).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Never</span>
                      )}
                      {staff.pending_review && (
                        <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          Scheduled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      {staff.last_review_rating ? (
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={`star-${staff.staff_id}-${star}`}
                              className={`w-4 h-4 ${star <= staff.last_review_rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600'}`}
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewReview(staff.staff_id)}
                          className="flex items-center gap-1"
                          data-testid={`view-review-${staff.staff_id}`}
                        >
                          <Eye className="w-4 h-4" />
                          <span className="hidden sm:inline">View</span>
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => { setSelectedStaff(staff); setShowScheduleModal(true); }}
                          className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white"
                          data-testid={`schedule-review-${staff.staff_id}`}
                        >
                          <Plus className="w-4 h-4" />
                          <span className="hidden sm:inline">Schedule</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredStaff.length === 0 && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">No staff members found</p>
            </div>
          )}
        </div>
      )}

      {/* Schedule Review Modal */}
      {showScheduleModal && selectedStaff && (
        <ScheduleReviewModal
          staff={selectedStaff}
          onClose={() => { setShowScheduleModal(false); setSelectedStaff(null); }}
          onSchedule={handleScheduleReview}
        />
      )}
    </div>
  );
}
